import { periodDay } from "./cycle";
import { todayStr } from "./date";
import { kmToSteps, phaseKcal, phaseKm } from "./energy";
import { phase } from "./plan";
import type { KayaState, TreadmillPhase, TreadmillTotals } from "./types";

/** The interval protocol for today.
 *  Phase 1 runs 7 short brisk/easy pairs; phase 2+ runs 5 longer 3-min pairs.
 *  Period days get a flat easy walk instead, unless the user overrode it for the day. */
/**
 * Whether this person has a treadmill at all.
 *
 * Off, the guided protocol, its speeds, the presets and the transcribed programs all go — they are
 * instructions for a machine that is not there. Free walks stay, and the tab becomes Walking:
 * somebody without a treadmill walks more, not less, so removing the walk with the machine would
 * be exactly the wrong half to take away.
 */
export function hasTreadmill(S: KayaState): boolean {
  return S.profile.hasTreadmill !== false;
}

/** `easy`: fast work is off for this person (`trainingQuestions.ts::sessionGate`) — the period
 *  day's flat easy walk, whatever the day. */
export function tmProtocol(S: KayaState, easy = false): TreadmillPhase[] {
  const p = phase(S);
  const pr = S.profile;
  const ph: TreadmillPhase[] = [];

  if (easy || (periodDay(S, todayStr()) && !S.tmNormalToday)) {
    ph.push({ l: "Easy walk", s: pr.easy1, secs: 1200, c: "easy" });
    ph.push({ l: "Cool-down", s: pr.easy1 - 0.7, secs: 120, c: "rest" });
    return ph;
  }

  if (p === 0) {
    // 5 + 7 × 3 + 4 = 30 minutes. The original's 4 and 3 made a 28-minute session under a
    // "30 min" promise; the rounds are the protocol, so the warm-up and cool-down take the two
    // minutes. Week 3+ is 2 + 5 × 6 + 2 = 34, and every label reads the real total. (ADR-036)
    ph.push({ l: "Warm-up", s: pr.easy1 - 0.5, secs: 300, c: "rest" });
    for (let r = 1; r <= 7; r++) {
      ph.push({ l: "Brisk " + r + "/7", s: pr.brisk1, secs: 120, c: "brisk", inc: pr.incline });
      ph.push({ l: "Easy " + r + "/7", s: pr.easy1, secs: 60, c: "easy" });
    }
    ph.push({ l: "Cool-down", s: pr.easy1 - 0.7, secs: 240, c: "rest" });
  } else {
    ph.push({ l: "Warm-up", s: pr.easy - 0.5, secs: 120, c: "rest" });
    for (let r = 1; r <= 5; r++) {
      ph.push({ l: "Brisk " + r + "/5", s: pr.brisk, secs: 180, c: "brisk", inc: pr.incline });
      ph.push({ l: "Easy " + r + "/5", s: pr.easy, secs: 180, c: "easy" });
    }
    ph.push({ l: "Cool-down", s: pr.easy - 0.7, secs: 120, c: "rest" });
  }
  return ph;
}

/** Today's protocol in words — the same branch `tmProtocol` takes, so the two never disagree. */
export function tmProtocolLine(S: KayaState, easy = false): string {
  if (easy) return "Easy steady walk · no brisk phases";
  if (periodDay(S, todayStr()) && !S.tmNormalToday) return "Period day · easy steady walk";
  return phase(S) === 0
    ? "Weeks 1–2 · 7 × (2 min brisk + 1 min easy)"
    : "Week 3+ · 5 × (3 min brisk + 3 min easy)";
}

/** Running totals through `upto` phases, plus an optional partial slice of the current one. */
export function tmTotals(
  S: KayaState,
  phases: TreadmillPhase[],
  upto: number,
  partialFrac?: number
): TreadmillTotals {
  let kcal = 0;
  let km = 0;
  phases.forEach((ph, i) => {
    if (i < upto) {
      kcal += phaseKcal(S, ph);
      km += phaseKm(ph);
    } else if (i === upto && partialFrac) {
      kcal += phaseKcal(S, ph) * partialFrac;
      km += phaseKm(ph) * partialFrac;
    }
  });
  return { kcal: Math.round(kcal), km, steps: kmToSteps(S, km) };
}

export function tmTotalSecs(phases: TreadmillPhase[]): number {
  return phases.reduce((a, p) => a + p.secs, 0);
}

export const TM_COLOR: Record<TreadmillPhase["c"], string> = {
  brisk: "var(--ochre)",
  easy: "var(--sky)",
  rest: "var(--rest)",
};
